// Spaces query layer. Pairs with `lib/views/spaces.ts` (server fetchers used
// during SSR prefetch) and `lib/mutations/spaces.ts`. No 'use client'
// directive: hooks are called from client components; server pages import
// the keys for prefetch wiring.
//
// After the 0015 cutover, post/comment query hooks have been folded into
// `lib/queries/feed.ts` — spaces consume the unified feed reads with
// `scope: 'group:<spaceId>'`.

import {
  infiniteQueryOptions,
  queryOptions,
  useQuery,
  useInfiniteQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import type { components } from '@leanin/api-client';
import { getApiClient } from '../api-client';
import { throwApiError, unwrap } from '../api-error';
import { trimLower } from './normalize';

export type SpaceSummary = components['schemas']['SpaceSummary'];
export type SpaceSummaryWithViewerRole =
  components['schemas']['SpaceSummaryWithViewerRole'];
export type SpaceSearchHit = components['schemas']['SpaceSearchHit'];
export type SpaceDetail = components['schemas']['SpaceDetail'];
export type SpaceMember = components['schemas']['GroupMemberView'];

export type SpaceScope = 'all' | 'mine' | 'invited';

export type SpacesListParams = {
  scope?: SpaceScope;
  pageSize?: number;
  /** Honored only when `scope === 'mine'`. Surfaces archived spaces the
   *  viewer leads so they appear under the Mine grid. */
  includeArchived?: boolean;
};

// The search endpoint rejects queries under 2 chars (422); below that the
// page falls back to its plain list + client-side narrowing.
export const MIN_SPACE_SEARCH_LENGTH = 2;

const DEFAULT_SPACE_SEARCH_PAGE_SIZE = 24;

type SpacesSearchArgs = {
  readonly q: string;
  readonly pageSize: number;
};

// ---- Query keys ----

export const spacesKeys = {
  all: ['spaces'] as const,
  lists: () => [...spacesKeys.all, 'list'] as const,

  // list — infinite + flat readers share this leaf's key. `params` is the
  // terminal normalized args; the cursor is injected via pageParam.
  list: (params: SpacesListParams = {}) => {
    const args: SpacesListParams = {
      ...(params.scope !== undefined ? { scope: params.scope } : {}),
      ...(params.pageSize !== undefined ? { pageSize: params.pageSize } : {}),
      ...(params.includeArchived !== undefined
        ? { includeArchived: params.includeArchived }
        : {}),
    };
    return infiniteQueryOptions({
      queryKey: [...spacesKeys.lists(), args] as const,
      queryFn: ({ pageParam }) =>
        fetchSpaces({ ...args, cursor: pageParam ?? undefined }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    });
  },

  // search — pageSize IS part of the key now (it shapes the response page),
  // so the same query at two page sizes is two cache entries (decision 4).
  search: (raw: { q: string; pageSize?: number }) => {
    const args: SpacesSearchArgs = {
      q: trimLower(raw.q),
      pageSize: raw.pageSize ?? DEFAULT_SPACE_SEARCH_PAGE_SIZE,
    };
    return queryOptions({
      queryKey: [...spacesKeys.all, 'search', args] as const,
      queryFn: () => fetchSearchSpaces(args),
      enabled: args.q.length >= MIN_SPACE_SEARCH_LENGTH,
    });
  },

  // search (infinite) — same normalized args + min-length gate as the plain
  // `search` leaf; the cursor is injected via pageParam so paging beyond the
  // first window no longer silently truncates.
  searchInfinite: (raw: { q: string; pageSize?: number }) => {
    const args: SpacesSearchArgs = {
      q: trimLower(raw.q),
      pageSize: raw.pageSize ?? DEFAULT_SPACE_SEARCH_PAGE_SIZE,
    };
    return infiniteQueryOptions({
      queryKey: [...spacesKeys.all, 'search', 'infinite', args] as const,
      queryFn: ({ pageParam }) =>
        fetchSearchSpaces({ ...args, cursor: pageParam ?? undefined }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      enabled: args.q.length >= MIN_SPACE_SEARCH_LENGTH,
    });
  },

  detail: (id: string) =>
    queryOptions({
      queryKey: [...spacesKeys.all, 'detail', id] as const,
      queryFn: () => fetchSpace(id),
    }),

  related: (id: string) =>
    queryOptions({
      queryKey: [...spacesKeys.all, 'detail', id, 'related'] as const,
      queryFn: () => fetchRelatedSpaces(id),
    }),
};

// ---- Client fetchers ----

export async function fetchSpaces(
  params: SpacesListParams & { cursor?: string },
) {
  const client = getApiClient();
  const data = unwrap(await client.GET('/api/v1/spaces', {
    params: {
      query: {
        scope: params.scope,
        pageSize: params.pageSize,
        cursor: params.cursor,
        ...(params.includeArchived ? { includeArchived: true } : {}),
      },
    },
  }));
  return data;
}

export async function fetchSpace(
  id: string,
): Promise<SpaceDetail | null> {
  const client = getApiClient();
  const { data, error, response } = await client.GET('/api/v1/spaces/{id}', {
    params: { path: { id } },
  });
  if (response.status === 404) return null;
  if (data === undefined) throwApiError(error, response);
  return data.data;
}

export async function fetchRelatedSpaces(id: string) {
  const client = getApiClient();
  const data = unwrap(await client.GET(
    '/api/v1/spaces/{id}/related',
    { params: { path: { id } } },
  ));
  return data.items;
}

export async function fetchSearchSpaces(
  args: SpacesSearchArgs & { cursor?: string },
) {
  const client = getApiClient();
  const data = unwrap(await client.GET('/api/v1/spaces/search', {
    params: {
      query: { q: args.q, pageSize: args.pageSize, cursor: args.cursor },
    },
  }));
  return data;
}

// ---- Hooks ----

export function useSpaces(
  params: SpacesListParams = {},
  options?: Omit<
    UseQueryOptions<
      Awaited<ReturnType<typeof fetchSpaces>>,
      Error,
      Awaited<ReturnType<typeof fetchSpaces>>
    >,
    'queryKey' | 'queryFn'
  >,
) {
  // Flat reader reusing the infinite list leaf's key (one cache entry shared
  // with `useInfiniteSpaces`); supplies its own non-paginated queryFn.
  return useQuery({
    queryKey: spacesKeys.list(params).queryKey,
    queryFn: () => fetchSpaces(params),
    ...options,
  });
}

export function useInfiniteSpaces(params: SpacesListParams = {}) {
  return useInfiniteQuery(spacesKeys.list(params));
}

export function useSpace(id: string) {
  return useQuery(spacesKeys.detail(id));
}

export function useRelatedSpaces(id: string) {
  return useQuery(spacesKeys.related(id));
}

export function useSearchSpaces(q: string, pageSize = DEFAULT_SPACE_SEARCH_PAGE_SIZE) {
  return useQuery(spacesKeys.search({ q, pageSize }));
}

export function useInfiniteSearchSpaces(
  q: string,
  options: { pageSize?: number; enabled?: boolean } = {},
) {
  const { pageSize = DEFAULT_SPACE_SEARCH_PAGE_SIZE, enabled } = options;
  const leaf = spacesKeys.searchInfinite({ q, pageSize });
  return useInfiniteQuery({
    ...leaf,
    enabled: leaf.enabled && (enabled ?? true),
  });
}
